/**
 * <card-media> — transparent product-card video (store-wide).
 *
 * Plays a transparent video over the product image on cards. Reads its <video> child:
 *   - WebM with alpha (VP9/VP8)        -> Chrome / Edge / Firefox / Android
 *   - HEVC/MOV with alpha (hvc1)       -> Safari / iOS  (must be hosted on an external CDN)
 *
 * `playback` attribute (from the global setting `card_video_playback`):
 *   always : autoplay everywhere (desktop + touch)
 *   hover  : play on hover (desktop) / autoplay (touch)
 *
 * Playback uses the declarative `<video autoplay>` attribute (set in product-card.liquid) plus a play() call.
 * Desktop and iOS Safari autoplay muted video fine. iOS Chrome (CriOS) refuses ALL autoplay until the first
 * user gesture (a third-party-iOS-browser policy that can't be bypassed), so we also register a one-time,
 * page-wide gesture fallback that starts the videos on the first tap/scroll — effectively autoplay while
 * browsing. On desktop "hover" mode this element instead cancels the autoplay and plays on hover.
 *
 * Reveal model (see card-video.css): the video is VISIBLE by default. On iOS, autoplay may be blocked, so we
 * must not gate visibility on the `playing` event firing — otherwise the product image shows instead of the
 * video. Showing the video means iOS displays the poster + native play control immediately (and plays when
 * autoplay is allowed). The `playing`/`pause` events below only drive the fade on DESKTOP hover mode, where
 * CSS keeps the video hidden until it actually starts playing on hover. Safari/iOS handle visibility natively.
 *
 * WebKit ignores WebM alpha (would show a black box), so on Safari/iOS we only keep the video when a
 * non-WebM (HEVC) source is actually playable — otherwise the video is removed and the product image shows.
 * Respects prefers-reduced-motion. No global side effects.
 *
 * Ported from the Hoamatkult store (Focal custom.js card-video logic).
 */

if (!customElements.get('card-media')) {
  customElements.define(
    'card-media',
    class CardMedia extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) {
          return;
        }

        // WebKit (Safari + every iOS browser) can't render WebM alpha. Drop the video unless a playable
        // HEVC (non-webm) source is present, so the product image shows instead of a black box.
        if (!this._canRenderAlpha()) {
          this.video.parentNode && this.video.parentNode.removeChild(this.video);
          return;
        }

        this.video.muted = true; // required for muted autoplay

        // Reveal the video (CSS opacity) from the ACTUAL playback state, not from our own play() call — so it
        // shows whether playback was started by the declarative `autoplay` attribute (the only reliable
        // trigger on iOS Chrome) or by us on hover. Safari/iOS then drive play/pause-on-visibility natively.
        this._onPlaying = () => this.setAttribute('playing', '');
        this._onPause = () => this.removeAttribute('playing');
        this.video.addEventListener('playing', this._onPlaying);
        this.video.addEventListener('pause', this._onPause);

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.video.removeAttribute('autoplay');
          this.video.pause();
          return; // keep the poster/image, never play
        }

        this.playback = this.getAttribute('playback') || 'hover';
        this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

        // Desktop + "hover": cancel autoplay and play on hover only.
        if (this.playback === 'hover' && this.canHover) {
          this.video.removeAttribute('autoplay');
          this.video.pause();
          this._play = () => this._safePlay();
          this._reset = () => {
            this.video.pause();
            try {
              this.video.currentTime = 0;
            } catch (e) {
              /* noop */
            }
          };
          this.card = this.closest('.product-card') || this.closest('.product-card__figure') || this;
          this.card.addEventListener('pointerenter', this._play);
          this.card.addEventListener('pointerleave', this._reset);
          return;
        }

        // Touch devices, or the "always" setting: keep the declarative `<video autoplay>` and also try play()
        // now. iOS Chrome (CriOS) refuses muted autoplay until the first user gesture — there is no way to
        // autoplay before any interaction in a third-party iOS browser. So register a single page-wide,
        // self-removing fallback that starts every pending card video on the first tap/scroll/keypress, when
        // iOS Chrome grants media playback. On a browse page that first gesture happens within a second, so it
        // behaves like autoplay. Desktop "always" and iOS Safari already autoplay, so play() succeeds there
        // immediately and the fallback is a harmless no-op.
        this._safePlay();
        CardMedia._registerGestureFallback(this);
      }

      _safePlay() {
        const promise = this.video && this.video.play();
        promise && typeof promise.catch === 'function' && promise.catch(() => {});
      }

      // One shared, self-removing gesture listener for ALL card videos — not a listener per card. iOS Chrome
      // grants media playback after the first user activation (tap/click/key), so we replay every pending
      // video then. `scroll` is intentionally NOT used: it does not count as a user activation in WebKit (but
      // a touch-scroll always fires `touchstart` first, which does).
      static _registerGestureFallback(instance) {
        (CardMedia._pending || (CardMedia._pending = [])).push(instance);
        if (CardMedia._gestureBound) {
          return;
        }
        CardMedia._gestureBound = true;
        const events = ['pointerdown', 'touchstart', 'keydown'];
        const fire = () => {
          const pending = CardMedia._pending || [];
          CardMedia._pending = [];
          CardMedia._gestureBound = false;
          events.forEach((evt) => window.removeEventListener(evt, fire));
          pending.forEach((el) => el._safePlay());
        };
        events.forEach((evt) => window.addEventListener(evt, fire, { passive: true }));
      }

      disconnectedCallback() {
        if (this.video) {
          this.video.removeEventListener('playing', this._onPlaying);
          this.video.removeEventListener('pause', this._onPause);
        }
        if (this.card) {
          this.card.removeEventListener('pointerenter', this._play);
          this.card.removeEventListener('pointerleave', this._reset);
        }
        if (CardMedia._pending) {
          const i = CardMedia._pending.indexOf(this);
          if (i !== -1) {
            CardMedia._pending.splice(i, 1);
          }
        }
      }

      _canRenderAlpha() {
        const isWebkit = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (!isWebkit) {
          return true; // Chromium / Firefox handle WebM alpha
        }
        const sources = this.video.querySelectorAll('source');
        for (let i = 0; i < sources.length; i++) {
          const type = sources[i].getAttribute('type') || '';
          if (type.indexOf('webm') === -1 && this.video.canPlayType(type.split(';')[0])) {
            return true;
          }
        }
        return false;
      }
    }
  );
}
